import React, { useCallback } from 'react';
import { removeConnections } from 'shared/apollo';
import styled from 'styled-components';
import { Program } from '../../Reports/NewReportDialog';
import { Task } from '../api';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import TrueImpactTextfieldWithIcon from 'shared/design-system/components/TrueImpactTextfieldWithIcon';
import {
  formatPercentageNumber,
  getAllocationStates,
  InvestmentSplitWithAnswers,
  isAllocationRecordValid,
} from './utils';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import InvestmentSplitButtons from './InvestmentSplitButtons';
import { NPOUser } from '../../ReportsBeta/NewProgram/api';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercentage, faCircleInfo } from '@fortawesome/pro-light-svg-icons';
// import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactDollarField from 'shared/design-system/components/TrueImpactDollarField';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { Tooltip } from '../../ReportBuilder/styles';
import { ApolloQueryResult } from 'apollo-boost';

const programTooltip = (
  <div>The program is the overall initiative being funded</div>
);
const yearTooltip = <div>The year the donation is used towards</div>;
const amountTooltip = (
  <div>
    The total amount of the donation you are allocating to the initiative
  </div>
);

interface InvestmentSplitProps extends ReturnType<typeof getAllocationStates> {
  task: Task;
  totalAmount: number;
  finishedIndexes: Set<number>;
  allPrograms: RequestState<{
    allPrograms: Program[];
  }>;
  allNPOUsers: RequestState<{
    allUsers: NPOUser[];
  }>;
  reloadTasks: () => unknown;
  reloadPrograms: () =>
    | Promise<
        ApolloQueryResult<{
          allPrograms: Program[];
        }>
      >
    | undefined;
  reloadNPOUser: () => unknown;
  selectedProgram: Program | undefined;
  programNameByDonor?: string;
  className?: string;
  investmentSplit: InvestmentSplitWithAnswers[];
  setInvestmentSplit: React.Dispatch<
    React.SetStateAction<InvestmentSplitWithAnswers[]>
  >;
}

const StyledDiv = styled.div`
  .investment-split-note {
    text-align: center;
    max-width: 645px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.medium};
  }
  .allocation-program-wrapper {
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    max-width: 1092px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.small};
    padding-left: ${SPACERS.small};
    .trash-icon {
      margin-top: ${SPACERS.small};
    }
    .allocation-field-label {
      margin-bottom: ${SPACERS.extraTiny};
      .info-icon {
        margin-left: ${SPACERS.extraTiny};
      }
    }
    .allocation-program-dropdown {
      flex: 1;
      min-width: 200px;
    }
    .allocation-amount-field {
      width: 140px;
    }
    .allocation-percentage-field {
      width: 100px;
    }
    .allocation-year-dropdown {
      width: 160px;
    }
    input {
      padding-top: 9px;
      padding-bottom: 9px;
      max-width: 200px;
    }
    .field-icon-amount {
      margin-left: ${SPACERS.extraTiny};
    }
    .field-icon-percentage {
      margin-right: ${SPACERS.tiny};
    }
  }
  .MuiSelect-root.MuiSelect-select {
    font-size: 14px;
    padding-top: 10px;
    padding-bottom: 10px;
  }
`;

const currentYear = new Date().getFullYear();
const getYears = (): number[] => {
  const years: number[] = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    years.push(i);
  }
  return years;
};
const years = getYears();
const InvestmentSplit: React.FC<InvestmentSplitProps> = ({
  task,
  allocationRecords,
  allocationAmountState,
  totalAmount,
  investmentSplit,
  finishedIndexes,
  setInvestmentSplit,
  allPrograms,
  allNPOUsers,
  reloadNPOUser,
  reloadPrograms,
  reloadTasks,
  selectedProgram,
  programNameByDonor,
  className,
}) => {
  const handleAdd = useCallback(() => {
    setInvestmentSplit(s => {
      const newState = [...s];
      newState.push({
        program: undefined,
        amount: '',
        percentage: '',
        answers: [],
      });
      return newState;
    });
  }, [setInvestmentSplit]);

  const handleProgramAdd = useCallback(
    async (programId: number) => {
      const r = await reloadPrograms();
      if (!r?.data?.allPrograms) return;
      const raw = r.data.allPrograms;
      const result: Program[] = Array.isArray(raw) ? raw : removeConnections(raw);
      const program = result.find((p: Program) => p.Id === programId);
      if (!program) {
        return;
      }
      setInvestmentSplit(s => {
        const newState = [...s];
        newState.push({
          program,
          amount: '',
          percentage: '',
          answers: [],
        });
        return newState;
      });
    },
    [reloadPrograms],
  );

  const handleDelete = useCallback(
    (index: number) => {
      setInvestmentSplit(s => {
        const newState = [...s];
        newState.splice(index, 1);
        return newState;
      });
    },
    [setInvestmentSplit],
  );

  const handleChange = useCallback(
    (p: Program, index: number) => {
      setInvestmentSplit(s => {
        const newState = [...s];
        newState[index].program = p;
        return newState;
      });
    },
    [setInvestmentSplit],
  );

  const handleAmountChange = useCallback(
    (a: string, index: number) => {
      const formattedAmount = a.replace(/,/g, '');
      const amount = Number(formattedAmount) || 0;
      setInvestmentSplit(s => {
        const newState = [...s];
        newState[index].amount = a;
        newState[index].percentage = formatPercentageNumber(
          (amount / totalAmount) * 100,
        );
        return newState;
      });
    },
    [setInvestmentSplit, totalAmount],
  );

  const handlePercentageChange = useCallback(
    (p: string, index: number) => {
      const percentage = Number(p) || 0;
      setInvestmentSplit(s => {
        const newState = [...s];
        newState[index].amount = formatPercentageNumber(
          (percentage / 100) * totalAmount,
        );
        newState[index].percentage = p;
        return newState;
      });
    },
    [setInvestmentSplit, totalAmount],
  );

  const handleYearChange = useCallback(
    (y: number, index: number) => {
      setInvestmentSplit(s => {
        const newState = [...s];
        newState[index].year = y;
        return newState;
      });
    },
    [setInvestmentSplit],
  );

  return (
    <StyledDiv className={className}>
      {selectedProgram ? (
        <TrueImpactText variant="body-small" className="investment-split-note">
          We have matched and allocated this donation to the program below. Feel
          free to adjust the allocation or add additional ones if this donation
          will be spilt among programs or years.
        </TrueImpactText>
      ) : programNameByDonor ? (
        <TrueImpactText variant="body-small" className="investment-split-note">
          Your donor identified the program for this donation as{' '}
          {programNameByDonor}, but a program with that exact name doesn’t exist.
          You can either add a new program to allocate this investment, or select
          another existing program from the dropdown below.
        </TrueImpactText>
      ) : (
        <TrueImpactText variant="body-small" className="investment-split-note">
          There are no existing programs in your portal that match. Add a new
          program to allocate this new investment.
        </TrueImpactText>
      )}
      {investmentSplit.map((investment, i) => {
        const investmentYear = Number(investment.year) || undefined;
        const isFinished = finishedIndexes.has(i);
        const isRecordValid = isAllocationRecordValid(
          allocationRecords,
          investment.program,
          investmentYear,
        );
        const onAmountChange = (v: string) => {
          handleAmountChange(v, i);
        };
        const onPercentageChange = (v: string) => {
          handlePercentageChange(v, i);
        };
        return (
          <div
            title={isFinished ? 'This investment is allocated!' : undefined}
            className="allocation-program-wrapper"
          >
            <div className="allocation-program-dropdown">
              <Tooltip interactive arrow={true} title={programTooltip}>
                <div>
                  <TrueImpactText
                    variant="body-small-bold"
                    className="allocation-field-label"
                  >
                    Program
                    {i === 0 && (
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="info-icon"
                        color={COLORS.ctaPrimary}
                      />
                    )}
                  </TrueImpactText>
                </div>
              </Tooltip>
              <TrueImpactDropdown
                name="Select a program"
                options={allPrograms.data?.allPrograms || []}
                getId={(p: Program) => p.Id}
                getLabel={(p: Program) => p.name}
                native={true}
                disabled={isFinished}
                getItemDisability={undefined}
                value={investment.program}
                renderValue={v => v?.name || 'Select a program'}
                borderColor={isRecordValid ? 'grayBase' : 'critical'}
                // getSelectorValues={(o: Program[], v: Program | undefined) => ({
                //   label: investment.program?.name || 'Select a program',
                //   isAnySelected: Boolean(investment),
                // })}
                onClear={undefined}
                onChange={(v: Program) => handleChange(v, i)}
              />
            </div>
            <div className="allocation-year-dropdown">
              <Tooltip interactive arrow={true} title={yearTooltip}>
                <div>
                  <TrueImpactText
                    variant="body-small-bold"
                    className="allocation-field-label"
                  >
                    Year
                    {i === 0 && (
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="info-icon"
                        color={COLORS.ctaPrimary}
                      />
                    )}
                  </TrueImpactText>
                </div>
              </Tooltip>
              <TrueImpactDropdown
                name="Select a year"
                options={years}
                getId={(p: number) => p}
                getLabel={(p: number) => p}
                native={true}
                disabled={isFinished}
                getItemDisability={undefined}
                value={investmentYear}
                borderColor={isRecordValid ? 'grayBase' : 'critical'}
                // getSelectorValues={(o: number[], v: number | undefined) => ({
                //   label: investment.year || 'Select a year',
                //   isAnySelected: Boolean(investment.year),
                // })}
                onClear={undefined}
                onChange={(v: number) => handleYearChange(v, i)}
              />
            </div>
            <div className="allocation-amount-field">
              <Tooltip interactive arrow={true} title={amountTooltip}>
                <div>
                  <TrueImpactText
                    variant="body-small-bold"
                    className="allocation-field-label"
                  >
                    Amount
                    {i === 0 && (
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="info-icon"
                        color={COLORS.ctaPrimary}
                      />
                    )}
                  </TrueImpactText>
                </div>
              </Tooltip>
              <TrueImpactDollarField
                value={numberWithCommas(investment.amount) || ''}
                onChange={onAmountChange}
                disabled={isFinished}
                toFixedDecimal={2}
                inputPaddingLeft="31px"
                iconTopPosition={11}
                iconLeftPosition={11}
                placeholder="0"
              />
              {/* <TrueImpactTextfieldWithIcon
                placeholder="0"
                state={allocationAmountState}
                value={investment.amount || ''}
                onChange={onAmountChange}
                side="left"
                innerBorder={false}
                sidePadding="8.5px"
                inputPadding="34px"
                disabled={isFinished}
                sideText={
                  <FontAwesomeIcon
                    icon={faDollar}
                    color={COLORS.ctaPrimary}
                    className="field-icon-amount"
                  />
                }
              /> */}
            </div>
            {investmentSplit.length > 1 && (
              <div className="allocation-percentage-field">
                <TrueImpactText
                  variant="body-small-bold"
                  className="allocation-field-label"
                >
                  Percentage
                </TrueImpactText>
                <TrueImpactTextfieldWithIcon
                  placeholder="0"
                  state={allocationAmountState}
                  value={investment.percentage || ''}
                  onChange={onPercentageChange}
                  side="right"
                  innerBorder={false}
                  disabled={isFinished}
                  sideText={
                    <FontAwesomeIcon
                      icon={faPercentage}
                      color={COLORS.ctaPrimary}
                      className="field-icon-percentage"
                    />
                  }
                />
              </div>
            )}
            <TrueImpactIconButton
              icon={faTrash}
              disabled={isFinished}
              onClick={() => handleDelete(i)}
              className="trash-icon"
            />
          </div>
        );
      })}
      <InvestmentSplitButtons
        allNPOUsers={allNPOUsers}
        allPrograms={allPrograms}
        reloadNPOUser={reloadNPOUser}
        reloadPrograms={handleProgramAdd}
        reloadTasks={reloadTasks}
        selectedProgram={selectedProgram}
        handleAdd={handleAdd}
      />
    </StyledDiv>
  );
};

export default InvestmentSplit;
